import './filter-editor.scss';

import _ from 'lodash';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import loglevel from 'loglevel';
const log = loglevel.getLogger('filter-editor');

import { FieldTypeCategory } from '@thinkalpha/table-client';

// import { ContentChangedEvent, FilterEditorDirective, Position, Range, Section, Selection } from '../../directives/filterEditor.directive';
import lexer from './lexer';
import { CompletionOption, Range, Section, Token, TokenType, CompletionType, CompletionContext, Field } from './model';
import parser from './parser.ast';
import {ParserResult} from './parser.ast';
import {flattenAst} from '../../components/filter-editor/ast';
import {useDeepEffect} from '../../hooks/useDeepEffect';

export type ContentChangedEvent = {value: string, range: Range, position: Position};

import classNames from 'classnames';
import { Editor, EditorState, convertFromRaw, ContentState, SelectionState, Modifier, convertToRaw, RawDraftEntityRange, RawDraftEntity, CompositeDecorator, DraftHandleValue, getDefaultKeyBinding, ContentBlock } from 'draft-js';
import generateRandomKey from 'draft-js/lib/generateRandomKey';
import annotateTokens from './section-generator';
import {CompletionList} from './completion-list';
import determinePosition, {Position} from '../../util/position';
import completer from './completer';
import { overlappingRange } from '../../util/overlappingRange';
import {Tooltip, Typography} from '@material-ui/core';
import {filterEntityRanges} from './entity-manip';

type Props = {
    fields: readonly Field[];
    value?: string;
    expressionOnlyMode?: boolean;
    placeholder?: string;
    noPlaceholderPromotion?: boolean;
    equalsMode?: boolean;
    dataTypeRequired?: FieldTypeCategory;

    onValueChange?: (value: string) => void;
    onParserResult?: (result?: ParserResult) => void;
    onValidityChange?: (valid: boolean | undefined, value?: string) => void;
};

const nbspRegex = new RegExp(String.fromCharCode(160), 'g');

const ErrorDecorator: React.FC<{contentState: ContentState, offsetKey: string, entityKey: string}> = ({contentState, offsetKey, children, entityKey}) => {
    const {errors}: {errors: string[]} = contentState.getEntity(entityKey).getData();
    return (
        <Tooltip
            title={<React.Fragment>
                <Typography color="inherit">Syntax Error</Typography>
                {errors.map((x, i) => <div key={i}>{x}</div>)}
            </React.Fragment>}
        >
            <span data-offset-key={offsetKey} className="error">
                {children}
            </span>
        </Tooltip>
    );
};

const decorators = new CompositeDecorator([
    {
        strategy: (contentBlock, callback, contentState) => {
            contentBlock.findEntityRanges(character => {
                const entityKey = character.getEntity();
                if (entityKey === null) {
                    return false;
                }
                return contentState.getEntity(entityKey).getType() === 'error';
            },
            callback);
        },
        component: ErrorDecorator,
    },
]);

const FilterEditor: React.FunctionComponent<Props> = ({equalsMode = false, noPlaceholderPromotion = false, dataTypeRequired, fields, placeholder, value: incomingValue, onValidityChange, onValueChange, onParserResult}) => {
    const [tokens, setTokens] = useState<Token[] | undefined>(undefined);
    const [parserResult, setParserResult] = useState<ParserResult>();
    const value = useRef<string | undefined>(); //incomingValue);
    const [editorState, setEditorState] = useState<EditorState>(EditorState.createWithContent(ContentState.createFromText(incomingValue || ''), decorators));
    const editorRef = useRef<Editor>(null);

    const [helperStartIndex, setHelperStartIndex] = useState<number | undefined>();
    const [helperPosition, setHelperPosition] = useState<Position | undefined>();
    const [completionContext, setCompletionContext] = useState<CompletionContext>();

    const divRef = useRef<HTMLDivElement>(null);
    const el = divRef.current;

    const pending = !parserResult || !value;

    const broadcastValueChange = useCallback((newValue: string) => {
        if (value.current === newValue) return;

        value.current = newValue;
        if (onValueChange) onValueChange(newValue);
    }, [onValueChange]);

    useEffect(() => {
        if (incomingValue === value.current) return;
        log.info('editor got new incoming value different from internal value... incoming:', incomingValue, 'internal:', value.current);
        value.current = incomingValue || '';
        
        let targetValue = incomingValue || '';
        if (equalsMode) {
            if (!targetValue.startsWith('=')) {
                // string filter
                setTokens([{type: TokenType.String, token: 'value', range: {start: 0, end: targetValue.length}}]);
                setParserResult(undefined);
                setEditorState(EditorState.createWithContent(ContentState.createFromText(targetValue), decorators));
                return;
            }

            targetValue = targetValue.substring(1);
        }

        const tokens = lexer(targetValue);
        setTokens(tokens);
        // console.log('set editor state due to incoming value change from', value.current, 'to', incomingValueOrInternalValue);
        setEditorState(EditorState.createWithContent(ContentState.createFromText(incomingValue || ''), decorators));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [equalsMode, incomingValue]);

    useDeepEffect(() => {
        if (!tokens) return;
        if (equalsMode && (!tokens.length || tokens[0].token[0] !== '=')) return; // equals mode shouldn't run through validator when there's no equals prefix

        const handle = setTimeout(() => {
            if (!tokens) return;
            // console.log('about to parse tokens', tokens);
            
            const res = parser(tokens!, fields);
            setParserResult(res);

            // console.log('setting editor state due to parser result');
            setEditorState(editorState => {
                let content = editorState.getCurrentContent();
                if (content.getPlainText('') !== value.current) {
                    log.debug('Bailing out due to value change, from', value.current, 'to', content.getPlainText(''));
                    return editorState;
                }

                const selection = editorState.getSelection();

                const blockKey: string = generateRandomKey();

                const sections = annotateTokens(tokens!, res, dataTypeRequired);
                const annotations = sections.filter(x => x.errors && x.errors.length).map<{range: RawDraftEntityRange, key: string, entity: RawDraftEntity}>(sect => {
                    const key: string = generateRandomKey();
                    return {
                        key,
                        range: {
                            key: key as any,
                            offset: sect.range.start,
                            length: sect.range.end - sect.range.start + 1
                        },
                        entity: {
                            data: { errors: sect.errors },
                            mutability: 'MUTABLE',
                            type: 'error'
                        }
                    };
                });

                content = convertFromRaw({
                    blocks: [
                        {
                            text: value.current,
                            type: 'unstyled',
                            entityRanges: annotations.map(x => x.range),
                            key: blockKey,
                            data: undefined,
                            depth: 0,
                            inlineStyleRanges: []
                        }
                    ],
                    entityMap: annotations.reduce((acc, x) => {acc[x.key] = x.entity; return acc;}, {})
                });
                
                let newState = EditorState.push(editorState, content, 'apply-entity');

                if (selection.getHasFocus()) {
                    const newSelection = selection.merge({focusKey: blockKey, anchorKey: blockKey}) as SelectionState;
                    newState = EditorState.forceSelection(newState, newSelection);
                    // log.debug('Forcing selection from', selection.toObject(), 'to', newSelection.toObject());
                } else {
                    // log.debug('Not forcing selection due to lack of focus.');
                }

                return newState;
            });

            setHelperNeeded(true);
        }, 500);
        return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokens]);

    useEffect(() => {
        // console.log('calling onparserresult with', parserResult);
        if (onParserResult) onParserResult(parserResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parserResult, onParserResult]);

    const valid = useMemo(() => {
        if (equalsMode && (!value.current || value.current[0] !== '=')) return true;

        if (!parserResult) return false;
        if (!parserResult.root) return false;
        if (!parserResult.valid) return false;
        const flat = flattenAst(parserResult.root);
        if (!flat.every(x => x.valid)) return false;
        if (dataTypeRequired !== undefined && parserResult.root.dataType !== dataTypeRequired) return false;

        return true;
    }, [dataTypeRequired, equalsMode, parserResult]);

    // if (document.activeElement && divRef.current && (document.activeElement === divRef.current || divRef.current.contains(document.activeElement))) {
    //     console.log('on this render, selection is', editorState.getSelection().toObject(), 'and text is', editorState.getCurrentContent().getPlainText(''));
    // }

    useEffect(() => {
        if (onValidityChange) onValidityChange(pending ? undefined : valid, value.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valid, pending]);

    const [helperNeeded, setHelperNeeded] = useState(false); // hack to make this happen on next render

    const onEditorChanged = useCallback((state: EditorState) => {
        // console.log('setting editor state due to editor change');
        let content = state.getCurrentContent();
        const newValue = content.getPlainText('');

        if (equalsMode && newValue[0] !== '=') {
            content = filterEntityRanges((c, ek, bl) => false, content) as ContentState;
            state = EditorState.push(state, content, 'apply-entity');
            setEditorState(state);
            if (onValidityChange) onValidityChange(undefined);

            return;
        }

        setEditorState(state);
        const targetValue = equalsMode ? newValue.substring(1) : newValue;
        setTokens(lexer(targetValue));
    }, [equalsMode, onValidityChange]);

    useEffect(() => {
        const content = editorState.getCurrentContent();
        const newValue = content.getPlainText('');
        broadcastValueChange(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorState]);

    const onEditorChangedRef = useRef(onEditorChanged);
    useEffect(() => { onEditorChangedRef.current = onEditorChanged; }, [onEditorChanged]);
    const onEditorChangedWrapper = (state: EditorState) => onEditorChangedRef.current(state);

    const focused = document.activeElement && el && (document.activeElement === el || el.contains(document.activeElement)) || false;
    const shrink = focused || !!value.current;
    
    const closeHelper = useCallback(() => {
        setHelperPosition(undefined);
        setHelperStartIndex(undefined);
    }, [setHelperPosition]);

    const onCompletionSelected = useCallback((option: CompletionOption) => {
        if (!tokens) return;
        if (!option) return;

        const lastToken = tokens[tokens.length - 1];
        let content = editorState.getCurrentContent();
        
        // const block = content.getFirstBlock();
        // const blockKey = block.getKey();
        const selection = editorState.getSelection();
        const location = selection.getHasFocus()
            ? selection.getFocusOffset()
            : (!lastToken ? 0 : lastToken.range.end);

        // console.log('reported location', location, 'due to focus', selection.getHasFocus());
        const currentTokens = tokens.filter(tk => overlappingRange(tk.range, location));
        // console.log('current tokens', currentTokens);
        // const tki = tokens.findIndex(tk => tk.range.start < location && tk.range.end + 1 >= location);
        const currentTokenIdx = currentTokens.findIndex(x => x.type === TokenType.Name);
        const currentToken = currentTokens[currentTokenIdx];
        const nextToken = currentTokens[currentTokenIdx + 1];

        // const midToken = currentToken !== undefined && currentToken.range.end + 1 >= location;

        // possible scenarios:
        // 1: we're in the middle of a name token
        // 3: we're at the end of a token

        let text = option.text;
        let offset = text.length;
        let newSelection = selection;

        if (option.type === CompletionType.function && (!nextToken || nextToken.token !== '(')) {
            text += '()';
            offset += 1;
        }

        if (currentToken) {
            // console.log('currenttoken', currentToken, 'so replacing it');
            const tokenSelection = selection.merge({anchorOffset: currentToken.range.start, focusOffset: currentToken.range.end}) as SelectionState;
            newSelection = newSelection.merge({anchorOffset: currentToken.range.start + offset, focusOffset: currentToken.range.start + offset}) as SelectionState;
            content = Modifier.replaceText(content, tokenSelection, text);
        } else {
            // console.log('no currenttoken name so inserting at', location);
            const tokenSelection = selection.merge({anchorOffset: location, focusOffset: location}) as SelectionState;
            newSelection = newSelection.merge({anchorOffset: location + offset, focusOffset: location + offset}) as SelectionState;
            content = Modifier.insertText(content, tokenSelection, text);
        }

        let newState = EditorState.push(editorState, content, 'insert-fragment');
        newState = EditorState.forceSelection(newState, newSelection);
        // console.log('setting editor state due to completion insertion');
        setEditorState(newState);

        const newValue = content.getFirstBlock().getText();
        setTokens(lexer(newValue));
        broadcastValueChange(newValue);
        // console.log('on value change', newValue);
        setHelperPosition(undefined);
        setHelperStartIndex(undefined);
    }, [tokens, editorState, broadcastValueChange]);

    const openHelper = useCallback(() => {
        if (equalsMode && (!value.current || !value.current.startsWith('='))) return;
        if (!document.activeElement || !divRef.current || (document.activeElement !== divRef.current && !divRef.current.contains(document.activeElement))) {
            return;
        }

        const position = determinePosition();
        const index = editorState.getSelection().getFocusOffset();

        setHelperPosition(position);
        setHelperStartIndex(index);
    }, [editorState, equalsMode]);

    useEffect(() => {
        if (!helperNeeded) return;

        setHelperNeeded(false);
        openHelper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [helperNeeded]);

    const keyCommand = useCallback((command: string, editorState: EditorState, eventTimestamp: number): DraftHandleValue => {
        if (command === 'helper-requested') {
            openHelper();
            return 'handled';
        }
        return 'not-handled';
    }, [openHelper]);

    const keyBinding = useCallback((evt: React.KeyboardEvent): string | null => {
        if (evt.ctrlKey && (evt.key === 'Space' || evt.key === ' ')) {
            return 'helper-requested';
        }
        if (evt.key === 'Enter') {
            evt.preventDefault();
            return 'enter';
        }
        return getDefaultKeyBinding(evt);
    }, []);

    useEffect(() => {
        if (!parserResult) return;
        if (helperStartIndex === undefined) return;
        if (!tokens) return;

        const currentSelection = editorState.getSelection();
        const currentPosition = currentSelection.getFocusOffset();

        // const currentTokens = tokens && tokens.filter(tk => overlappingRange(tk.range, currentPosition));
        // const currentToken = currentTokens && currentTokens[currentTokens.length - 1];
        const context = completer(value.current || '', tokens, currentPosition,
            parserResult.text, parserResult.root, helperStartIndex,
            fields, dataTypeRequired);
        setCompletionContext(context);
    }, [tokens, fields, parserResult, editorState, helperStartIndex, dataTypeRequired]);

    const onFocus = useCallback((evt: React.FocusEvent) => {
        setTimeout(() => {
            // breathing room to allow acquisition of current position
            openHelper();
        }, 0);
    }, [openHelper]);

    const completionListRef = useRef<HTMLDivElement>(null);

    const onBlur = useCallback((evt: React.FocusEvent) => {
        const relatedTarget: HTMLElement | null = evt.relatedTarget as HTMLElement;
        if (relatedTarget && completionListRef.current && completionListRef.current.contains(relatedTarget)) {
            // hack to not hide completions if we are clicking an option
            evt.preventDefault();
            // editorRef.current!.focus();
            return;
        }
        setHelperPosition(undefined);
    }, []);

    return (<>
        <div ref={divRef} className={classNames({'filter-editor': true, valid: !pending && valid, invalid: !pending && !valid, pending})}>
            {placeholder && <label hidden={shrink && noPlaceholderPromotion} className={classNames({shrink})}>{placeholder}</label>}
            <Editor
                ref={editorRef}
                editorState={editorState}
                onChange={onEditorChangedWrapper}
                handleKeyCommand={keyCommand}
                keyBindingFn={keyBinding}
                onFocus={onFocus}
                onBlur={onBlur}
            />
        </div>
        <CompletionList
            ref={completionListRef}
            context={completionContext}
            position={helperPosition}
            onSelect={onCompletionSelected}
            onClose={closeHelper}
        />
    </>);
};

export default FilterEditor;